/**
 * FS-07 자유 놀이터 배치 초안(SR-MODE-06 · 계약 §0 · §1.2) — 교사 배치 도구의 순수 연산.
 *  - 바닥 24 × 20 m(가운데 z = 2 — 룸 서버 SANDBOX_FLOOR 와 같다) · 0.5 m 격자에 맞춘다 · 물건 ≤50
 *  - 초안은 화면에만 있고 '✓ 배치 끝'에 한 번 보낸다(월드 다시 싣기 · 기계는 자리로)
 *  - 컨베이어(1 × 2 m · 화살표 쪽으로 흐른다) · 이동 발판(1 × 1 m 판이 세로 3 m 길을 6초에 왕복 — 놓은 자리가 길 가운데):
 *    S4(2026-09-28 · SR-PHY-04) 프리팹이 생겨 '곧' 을 풀었다. 차지하는 크기 = 룸 서버 SANDBOX_SPEC(이동 발판은 길 전체)
 */
import type { SandboxObject, SandboxPrefab, SandboxDraft } from "../types";

export const SANDBOX_MAX = 50;
export const FLOOR = { x: 24, z: 20, cz: 2 } as const;
export const SNAP_M = 0.5;

/** 팔레트 순서(와이어) */
export const PALETTE: readonly { key: SandboxPrefab }[] = [
  { key: "ball" },
  { key: "crate" },
  { key: "log" },
  { key: "ramp" },
  { key: "wall" },
  { key: "conveyor" },
  { key: "mover" },
];

/** 바닥에서 차지하는 크기(가로 x · 세로 z · m — yaw 0 기준 · 룸 서버 SANDBOX_SPEC 과 같다) */
export const FOOTPRINT: Record<SandboxPrefab, [number, number]> = {
  ball: [0.3, 0.3],
  crate: [0.5, 0.5],
  log: [1.0, 0.3],
  ramp: [1.0, 1.2],
  wall: [2.0, 0.1],
  conveyor: [1.0, 2.0],
  // 판 1 m + 길 2 m(세로 z · yaw 로 돈다)
  mover: [1.0, 3.0],
};

export const isPrefab = (v: unknown): v is SandboxPrefab => typeof v === "string" && v in FOOTPRINT;

/**
 * 인벤토리 묶음(2026-10-11 — 운영 무대 꾸밈 인벤토리와 같은 틀): 굴러가요 · 쌓아요 · 길 · 장치.
 * 묶음 안 순서는 팔레트 순서({@link PALETTE})와 같다.
 */
export const INVENTORY: readonly {
  cat: "roll" | "build" | "path";
  kinds: readonly SandboxPrefab[];
}[] = [
  { cat: "roll", kinds: ["ball", "log"] },
  { cat: "build", kinds: ["crate", "wall"] },
  { cat: "path", kinds: ["ramp", "conveyor", "mover"] },
];
export type InventoryCat = (typeof INVENTORY)[number]["cat"];

/**
 * 미리보기 창 모양(렌더러 EnvPreviewSpec) — 크기(가로 x · 높이 y · 깊이 z · m) = 룸 서버 SANDBOX_SPEC(libs/room-sim modes.ts) · 표면 = 프리팹 표면(libs/modes prefabs).
 * 이동 발판은 판 1 × 1 m 가 세로 2 m 를 오간다(travel).
 */
export const PREVIEW: Record<
  SandboxPrefab,
  {
    shape: "box" | "sphere" | "ramp";
    size: readonly [number, number, number];
    surface: string;
    accent?: "belt" | "mover";
    travel?: number;
  }
> = {
  ball: { shape: "sphere", size: [0.3, 0.3, 0.3], surface: "ball" },
  crate: { shape: "box", size: [0.5, 0.4, 0.5], surface: "crate" },
  log: { shape: "box", size: [1.0, 0.25, 0.3], surface: "crate" },
  ramp: { shape: "ramp", size: [1.0, 0.3, 1.2], surface: "ramp" },
  wall: { shape: "box", size: [2.0, 0.5, 0.1], surface: "wall" },
  conveyor: { shape: "box", size: [1.0, 0.02, 2.0], surface: "conveyor", accent: "belt" },
  mover: { shape: "box", size: [1.0, 0.02, 1.0], surface: "mover", accent: "mover", travel: 2 },
};

/** 두 물건의 바닥 자리가 겹치나(돌린 크기 기준) */
function overlaps(
  a: { at: readonly number[]; w: number; d: number },
  b: { at: readonly number[]; w: number; d: number },
): boolean {
  return (
    Math.abs(a.at[0]! - b.at[0]!) * 2 < a.w + b.w && Math.abs(a.at[2]! - b.at[2]!) * 2 < a.d + b.d
  );
}

/**
 * '＋ 놓기'(인벤토리) 자리 — 바닥 가운데에서 바깥으로 1 m 씩 돌며 다른 물건과 겹치지 않는 첫 격자 자리(바닥 안).
 * 빈자리가 없으면 가운데(교사가 끌어 옮긴다).
 */
export function freeSpot(d: SandboxDraft, prefab: SandboxPrefab): { x: number; z: number } {
  const [w, dd] = FOOTPRINT[prefab];
  const taken = d.objects.map((o) => {
    const [ow, od] = sizeOf(o);
    return { at: o.at, w: ow, d: od };
  });
  for (let r = 0; r <= 9; r++)
    for (let i = -r; i <= r; i++)
      for (const [dx, dz] of [
        [i, -r],
        [i, r],
        [-r, i],
        [r, i],
      ] as const) {
        const at = placeAt({ prefab }, dx, FLOOR.cz + dz);
        if (!taken.some((t) => overlaps({ at, w, d: dd }, t))) return { x: at[0], z: at[2] };
      }
  return { x: 0, z: FLOOR.cz };
}

const snap = (v: number) => Math.round(v / SNAP_M) * SNAP_M;
/** 격자에 맞추고 [lo, hi] 안의 격자점으로(끝이 격자가 아니면 안쪽 격자점) */
const snapIn = (v: number, lo: number, hi: number) =>
  Math.min(Math.floor(hi / SNAP_M) * SNAP_M, Math.max(Math.ceil(lo / SNAP_M) * SNAP_M, snap(v)));

/** 돌린 뒤 크기(90 · 270 이면 가로세로 바뀜) */
export function sizeOf(o: Pick<SandboxObject, "prefab" | "yaw">): [number, number] {
  const [w, d] = FOOTPRINT[o.prefab];
  return o.yaw === 90 || o.yaw === 270 ? [d, w] : [w, d];
}

/** 격자에 맞추고 바닥 안으로(물건 반 크기만큼 안쪽) */
export function placeAt(
  o: Pick<SandboxObject, "prefab" | "yaw">,
  x: number,
  z: number,
): [number, number, number] {
  const [w, d] = sizeOf(o);
  const hx = FLOOR.x / 2 - w / 2;
  const hz = FLOOR.z / 2 - d / 2;
  return [snapIn(x, -hx, hx), 0, snapIn(z, FLOOR.cz - hz, FLOOR.cz + hz)];
}

export function draftOf(objects: readonly SandboxObject[]): SandboxDraft {
  const nums = objects.map((o) => Number(/^o(\d+)$/.exec(o.id)?.[1] ?? 0));
  return { objects: objects.slice(0, SANDBOX_MAX), seq: Math.max(0, ...nums) + 1 };
}

/** 놓기 — 50개면 그대로(같은 초안) */
export function add(d: SandboxDraft, prefab: SandboxPrefab, x: number, z: number): SandboxDraft {
  if (d.objects.length >= SANDBOX_MAX) return d;
  const o: SandboxObject = { id: `o${d.seq}`, prefab, at: placeAt({ prefab }, x, z) };
  return { objects: [...d.objects, o], seq: d.seq + 1 };
}

export function move(d: SandboxDraft, id: string, x: number, z: number): SandboxDraft {
  return {
    ...d,
    objects: d.objects.map((o) => (o.id === id ? { ...o, at: placeAt(o, x, z) } : o)),
  };
}

/** 90° 돌리기(바닥 안으로 다시 맞춘다) */
export function rotate(d: SandboxDraft, id: string): SandboxDraft {
  return {
    ...d,
    objects: d.objects.map((o) => {
      if (o.id !== id) return o;
      const yaw = (((o.yaw ?? 0) + 90) % 360) as 0 | 90 | 180 | 270;
      const next = { ...o, yaw };
      return { ...next, at: placeAt(next, o.at[0], o.at[2]) };
    }),
  };
}

export function remove(d: SandboxDraft, id: string): SandboxDraft {
  return { ...d, objects: d.objects.filter((o) => o.id !== id) };
}

export function clearAll(d: SandboxDraft): SandboxDraft {
  return { ...d, objects: [] };
}

/** 서버 모양 그대로(yaw 0 은 뺀다) */
export function payload(d: SandboxDraft): SandboxObject[] {
  return d.objects.map((o) =>
    o.yaw
      ? { id: o.id, prefab: o.prefab, at: o.at, yaw: o.yaw }
      : { id: o.id, prefab: o.prefab, at: o.at },
  );
}

/** 두 배치가 같은가(초안이 바뀌었나) */
export function sameLayout(a: readonly SandboxObject[], b: readonly SandboxObject[]): boolean {
  const key = (xs: readonly SandboxObject[]) =>
    JSON.stringify(
      [...xs]
        .map((o) => [o.id, o.prefab, o.at, o.yaw ?? 0])
        .sort((p, q) => String(p[0]).localeCompare(String(q[0]))),
    );
  return key(a) === key(b);
}

/** FullState meta.mode.objects → 물건 목록(모르는 모양은 뺀다) */
export function readObjects(v: unknown): SandboxObject[] {
  return (Array.isArray(v) ? v : [])
    .filter(
      (o): o is SandboxObject =>
        !!o &&
        typeof o === "object" &&
        typeof (o as SandboxObject).id === "string" &&
        isPrefab((o as SandboxObject).prefab) &&
        Array.isArray((o as SandboxObject).at) &&
        (o as SandboxObject).at.length === 3,
    )
    .map((o) => ({ ...o, at: [Number(o.at[0]), Number(o.at[1]), Number(o.at[2])] }));
}
